import { useCallback, useState } from 'react';

/** `crypto.randomUUID()` with a fallback for old browsers and insecure contexts. */
export const newUuid = (): string =>
	typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
		? crypto.randomUUID()
		: 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
				const r = Math.floor(Math.random() * 16);
				return (c === 'x' ? r : (r % 4) + 8).toString(16);
			});

/**
 * Idempotency key of a money-moving dialog: generated again every time the dialog opens, so a
 * repeated click of the same opening is a repeat for the gateway and cannot charge twice (03 §4.8).
 * `renew` gives a new key after a final failure.
 */
export const useIdempotencyKey = (open: boolean): [string, () => void] => {
	const [key, setKey] = useState(newUuid);
	const [wasOpen, setWasOpen] = useState(open);
	if (open !== wasOpen) {
		setWasOpen(open);
		if (open) setKey(newUuid());
	}
	const renew = useCallback(() => setKey(newUuid()), []);
	return [key, renew];
};

/** Saves a downloaded blob as a file. */
export const saveBlob = (blob: Blob, fileName: string): void => {
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	link.remove();
	window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/** Copies text to the clipboard; `false` when the browser refuses. */
export const copyText = async (text: string): Promise<boolean> => {
	try {
		await navigator.clipboard.writeText(text);
		return true;
	} catch {
		return false;
	}
};

/** Counter that grows on every call of the setter: a cheap "reload the list" signal. */
export const useReloadKey = (): [number, () => void] => {
	const [key, setKey] = useState(0);
	const bump = useCallback(() => setKey((value) => value + 1), []);
	return [key, bump];
};
